import { useEffect, useRef, useState } from 'react';

export type EventType = 'hello' | 'draft.published' | 'draft.updated' | 'change.created' | 'change.decided' | 'thread.created' | 'thread.updated' | 'comment.created' | 'plugin.status';
export const EVENT_TYPES: EventType[] = ['hello', 'draft.published', 'draft.updated', 'change.created', 'change.decided', 'thread.created', 'thread.updated', 'comment.created', 'plugin.status'];

export type EventHandler = (type: EventType, data: unknown) => void;
export type ConnectionState = 'connecting' | 'open' | 'disconnected';

/** A pluggable source so fixture mode can feed the same hook from an in-memory bus. */
export interface EventSourceLike {
  subscribe(handler: EventHandler, onState: (s: ConnectionState) => void): () => void;
}

let source: EventSourceLike | undefined;

export function setEventSource(s: EventSourceLike): void { source = s; }

function sseSource(): EventSourceLike {
  return {
    subscribe(handler, onState) {
      const es = new EventSource('/api/events', { withCredentials: true });
      onState('connecting');
      es.onopen = () => onState('open');
      es.onerror = () => onState('disconnected');
      const parse = (raw: string): unknown => { try { return JSON.parse(raw); } catch { return raw; } };
      for (const t of EVENT_TYPES) es.addEventListener(t, (ev) => handler(t, parse((ev as MessageEvent).data)));
      // tolerate servers that send unnamed messages shaped {type, data}
      es.onmessage = (ev) => {
        const d = parse(ev.data) as { type?: string; data?: unknown };
        if (d && typeof d === 'object' && typeof d.type === 'string' && EVENT_TYPES.includes(d.type as EventType)) handler(d.type as EventType, d.data);
      };
      return () => es.close();
    },
  };
}

export function subscribeEvents(handler: EventHandler, onState: (s: ConnectionState) => void = () => {}): () => void {
  return (source ?? (source = sseSource())).subscribe(handler, onState);
}

/**
 * Subscribe for the lifetime of a component. The handler ref is updated so callers can pass
 * inline closures. `enabled` false never opens the stream: a share-link guest has no SSE, and an
 * EventSource the server answers 403 would otherwise sit there reconnecting.
 */
export function useServerEvents(handler: EventHandler, enabled = true): ConnectionState {
  const ref = useRef(handler);
  ref.current = handler;
  const [state, setState] = useState<ConnectionState>(enabled ? 'connecting' : 'disconnected');
  useEffect(() => {
    if (!enabled) return;
    return subscribeEvents((t, d) => ref.current(t, d), setState);
  }, [enabled]);
  return state;
}
